/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Which route keys a tenant may reach, decided from the module registry.
 *
 * The registry lives in the backend (app/Services/module_registry.py) and
 * reaches this file two ways: live, as the `catalog` on GET
 * /module-entitlements, and as the checked-in snapshot in
 * generated/moduleRegistry.json, which is what applies until the first
 * response arrives and what moduleEntitlementMap.test.js checks routes.js
 * against. Nothing here assigns a route key to a module; that is done in the
 * registry, so the backend guard and this filter cannot drift.
 *
 * Every routed key must be one of: a core key (below), a module's route key,
 * a module's settings key (always routed, the page gates itself), or a custom
 * object key. Anything else is refused once entitlements are known. That is
 * deliberate: a page that nobody classified used to be visible to every
 * tenant, which is how unpaid features leak.
 */

import registrySnapshot from "generated/moduleRegistry.json";

/**
 * Route keys reachable by every tenant regardless of subscription. Not module
 * surfaces: the shell (dashboard, settings, profile, search), auth and public
 * pages, and the reports module, which is not sold separately (what a report
 * may reach is decided per object by the permission grid and the tenant's
 * entitlements inside the catalog endpoint). Module configuration pages that
 * an admin must reach before buying the module are NOT listed here; they are
 * the module's settings_route_keys in the registry.
 */
export const CORE_ROUTE_KEYS = new Set([
  "dashboard",
  "settings",
  "company-settings",
  "payment-settings",
  "payment-gateway-setup",
  "email-to-case-settings",
  // Reachable by route; the pages themselves check the MANAGE_ROLES /
  // MANAGE_TEAMS capability and render access-denied.
  "roles-permissions",
  "role-detail",
  "teams-settings",
  // The v2 user record; the page needs MANAGE_USERS (GET /users/{id}/access).
  "settings-user-record",
  "inbox",
  "reports",
  "email-setup",
  "user-management",
  "profile",
  "agent-map",
  "search",
  "create-password",
  "reset-password",
  "gmail-auth-callback",
  "customer-360-view",
  "support-ticket",
  "view-user-profile",
  "automation-builder",
  "custom-module",
  "dashboards",
  "dashboard-detail",
  "report-new",
  "report-edit",
  "report-detail",
  // OAuth: anyone may review and disconnect the apps they authorised, and
  // the consent screen is reachable by every user an app sends here.
  "connected-apps",
  "oauth-consent",
  // Public, unauthenticated pages.
  "public-pay",
  "travel-order-payment",
  "merchant-allocation-respond",
  "merchant-order-line-respond",
]);

/** Derive every lookup this file needs from a registry catalog. */
function buildModuleMaps(modules) {
  const codeToRouteKeys = {};
  const routeKeyToModule = {};
  const settingsRouteKeys = new Set();
  const sidenavIdToModule = {};
  const sidenavMeta = {};
  // Module code -> industry edition (null for core modules). Taken from the
  // registry's `edition` on each module, so a new edition's modules are gated
  // by it without a hand list here.
  const moduleToEdition = {};
  (Array.isArray(modules) ? modules : []).forEach((m) => {
    if (!m?.code) return;
    const code = String(m.code).toUpperCase();
    codeToRouteKeys[code] = [...(m.route_keys || [])];
    moduleToEdition[code] = m.edition ? String(m.edition).toUpperCase() : null;
    (m.route_keys || []).forEach((key) => {
      routeKeyToModule[key] = code;
    });
    (m.settings_route_keys || []).forEach((key) => settingsRouteKeys.add(key));
    (m.sidenav || []).forEach((item) => {
      if (!item?.id) return;
      sidenavIdToModule[item.id] = code;
      sidenavMeta[item.id] = {
        name: item.name,
        icon: item.icon,
        defaultSection: item.section,
        ...(item.nested ? { nested: true } : {}),
      };
    });
  });
  return {
    codeToRouteKeys,
    routeKeyToModule,
    settingsRouteKeys,
    sidenavIdToModule,
    sidenavMeta,
    moduleToEdition,
    alwaysAllowed: new Set([...CORE_ROUTE_KEYS, ...settingsRouteKeys]),
  };
}

const SNAPSHOT_MAPS = buildModuleMaps(registrySnapshot.modules);
const catalogMapCache = new WeakMap();

/**
 * Lookups for a live catalog (the array from /module-entitlements), or the
 * snapshot when none has arrived. Cached per catalog array so callers can
 * pass it through memoised options without rebuilding.
 */
export function getModuleMaps(catalog) {
  if (!Array.isArray(catalog) || catalog.length === 0) return SNAPSHOT_MAPS;
  let maps = catalogMapCache.get(catalog);
  if (!maps) {
    maps = buildModuleMaps(catalog);
    catalogMapCache.set(catalog, maps);
  }
  return maps;
}

/** Module code -> route keys, from the snapshot. Kept for existing importers. */
export const MODULE_CODE_TO_ROUTE_KEYS = SNAPSHOT_MAPS.codeToRouteKeys;

export const TRAVEL_ROUTE_KEYS = new Set(MODULE_CODE_TO_ROUTE_KEYS.TRAVEL || []);

export const BUILTIN_MODULE_ROUTE_KEYS = new Set(
  Object.entries(MODULE_CODE_TO_ROUTE_KEYS).flatMap(([code, keys]) =>
    code === "TRAVEL" ? [] : keys
  )
);

/** Core keys plus every module's settings pages, from the snapshot. */
export const ALWAYS_ALLOWED_ROUTE_KEYS = SNAPSHOT_MAPS.alwaysAllowed;

export const CUSTOM_OBJECT_ROUTE_KEYS = new Set([
  "custom-objects",
  "custom-object-list",
  "custom-object-detail",
  "custom-objects-settings",
  "custom-object-designer-new",
  "custom-object-designer-edit",
]);

export function routeKeyToModuleCode(routeKey, catalog) {
  return getModuleMaps(catalog).routeKeyToModule[routeKey] || null;
}

/**
 * Editions implied by a set of module codes: an edition is held when any of
 * its modules is. Used until a /module-entitlements response that carries
 * `editions` arrives (and for an older backend that never sends it). A live
 * catalog's per-module `edition` counts as well as the edition catalog's
 * module lists (the snapshot's by default).
 */
export function deriveEditionsFromModules(moduleCodeSet, catalog = null, editionCatalog = null) {
  const held = new Set();
  if (!moduleCodeSet || moduleCodeSet.size === 0) return held;
  (Array.isArray(catalog) ? catalog : registrySnapshot.modules || []).forEach((m) => {
    if (m?.edition && m?.code && moduleCodeSet.has(String(m.code).toUpperCase())) {
      held.add(String(m.edition).toUpperCase());
    }
  });
  (Array.isArray(editionCatalog) ? editionCatalog : registrySnapshot.editions || []).forEach((e) => {
    if (!e?.code) return;
    if ((e.module_codes || []).some((code) => moduleCodeSet.has(String(code).toUpperCase()))) {
      held.add(String(e.code).toUpperCase());
    }
  });
  return held;
}

/**
 * Whether an edition-tagged column of a core object is visible: true unless
 * the column is tagged and the tenant holds none of its editions.
 */
export function isFieldVisibleFor(fieldTags, hasEdition, objectApiName, column) {
  const tags = fieldTags?.[objectApiName]?.[column];
  if (!Array.isArray(tags) || tags.length === 0) return true;
  return tags.some((code) => hasEdition(code));
}

/** The industry edition a module belongs to, or null for a core module. */
export function moduleCodeToEdition(moduleCode, catalog) {
  if (!moduleCode) return null;
  return getModuleMaps(catalog).moduleToEdition[String(moduleCode).toUpperCase()] || null;
}

/**
 * Legacy commerce modules replaced by Travel catalog / travel orders when the
 * company travel toggle is on. Used by router + sidenav so deep links and nav
 * cannot disagree.
 */
export const LEGACY_CRM_MODULES_HIDDEN_WHEN_TRAVEL = new Set([
  "PRODUCTS",
  "QUOTATIONS",
  "ORDERS",
]);

/**
 * Whether a module the tenant may otherwise reach is allowed by its edition.
 *
 * Shared by the router filter and the sidenav so the two cannot disagree.
 * - Core modules (no edition): the module code decides.
 * - TRAVEL edition: the TRAVEL entitlement AND the company-level
 *   is_travel_enabled toggle, as before editions existed. The module-code check
 *   is not repeated because hasTravelEntitlement already is that check.
 * - Any other edition: the module code, and the edition itself when the
 *   caller knows the tenant's editions (enabledEditions). No company toggle.
 * - When travel is on, legacy PRODUCTS / QUOTATIONS / ORDERS are hidden even
 *   if those modules are still on the subscription. Not in v2 (isV2): there
 *   the app registry keeps them in the Sales app and out of the Travel app, so
 *   a travel tenant can still use standard orders. Their module codes decide.
 */
export function isEditionModuleAllowed(moduleCode, options = {}) {
  const {
    enabledModuleCodes = null,
    enabledEditions = null,
    isTravelEnabled = true,
    hasTravelEntitlement = true,
    moduleCatalog = null,
    isV2 = false,
  } = options;
  const code = moduleCode ? String(moduleCode).toUpperCase() : null;
  if (!isV2 && isTravelEnabled && code && LEGACY_CRM_MODULES_HIDDEN_WHEN_TRAVEL.has(code)) {
    return false;
  }
  const edition = moduleCodeToEdition(moduleCode, moduleCatalog);
  if (edition === "TRAVEL" || code === "TRAVEL") {
    return Boolean(hasTravelEntitlement && isTravelEnabled);
  }
  const codes =
    enabledModuleCodes instanceof Set
      ? enabledModuleCodes
      : new Set(
          (Array.isArray(enabledModuleCodes) ? enabledModuleCodes : []).map((c) =>
            String(c).toUpperCase()
          )
        );
  if (!codes.has(code)) return false;
  if (edition && enabledEditions instanceof Set && !enabledEditions.has(edition)) {
    return false;
  }
  return true;
}

export function isRouteKeyEntitled(routeKey, options = {}) {
  const {
    enabledModuleCodes = null,
    // Set of edition codes the tenant holds. Optional: without it an edition
    // module is decided by its module code alone.
    enabledEditions = null,
    isTravelEnabled = true,
    hasTravelEntitlement = true,
    isCustomObjectsEnabled = false,
    entitlementsReady = true,
    moduleCatalog = null,
    // Permission gate. Applied AFTER the entitlement logic below, so an
    // unlicensed module still reports as an entitlement problem rather than a
    // permission one. Defaults keep every existing caller unchanged.
    permissionMode = "legacy",
    permissionsReady = false,
    allowedRouteKeys = null,
    governedRouteKeys = null,
    // v2 shell: legacy commerce modules stay reachable with travel on.
    isV2 = false,
  } = options;

  const maps = getModuleMaps(moduleCatalog);

  if (!routeKey || maps.alwaysAllowed.has(routeKey)) {
    return true;
  }

  // Avoid blank deep-links while entitlements/feature licenses are still loading.
  if (!entitlementsReady) {
    return true;
  }

  if (CUSTOM_OBJECT_ROUTE_KEYS.has(routeKey) || routeKey.startsWith("co_")) {
    return isCustomObjectsEnabled && isPermitted(routeKey);
  }

  const moduleCode = maps.routeKeyToModule[routeKey] || null;

  // Modules are gated by their code and, for an industry edition, by the
  // edition (Travel additionally by the company-level flag).
  if (moduleCode) {
    return (
      isEditionModuleAllowed(moduleCode, {
        enabledModuleCodes,
        enabledEditions,
        isTravelEnabled,
        hasTravelEntitlement,
        moduleCatalog,
        isV2,
      }) && isPermitted(routeKey)
    );
  }

  // Not core, not a module's, not a custom object: refused. A key that
  // reaches here is a page nobody classified; moduleEntitlementMap.test.js
  // fails the build for it, so in practice this only guards a stale bundle
  // against a newer catalog.
  return false;

  /**
   * Permission gate, fail-open until the tenant is explicitly enforcing and the
   * snapshot has actually arrived.
   *
   * Kept inside this function on purpose. There are already three layers that
   * filter navigation (this one, App.js filterRoutesForSidenav, and
   * sidenavCatalog.isItemAllowed); they all sit downstream of the router filter,
   * so gating here reaches all of them. A fourth layer would be a fourth place
   * to forget.
   */
  function isPermitted(key) {
    if (permissionMode !== "enforce" || !permissionsReady) return true;
    if (!(allowedRouteKeys instanceof Set)) return true;
    // Only gate keys the server actually speaks for. It knows which object
    // governs a page for part of the app and not the rest, and a key it has no
    // opinion about must keep whatever the entitlement layers already decided
    // -- otherwise turning enforcement on removes pages nobody restricted.
    if (governedRouteKeys instanceof Set && !governedRouteKeys.has(key)) return true;
    return allowedRouteKeys.has(key);
  }
}

/**
 * The module a sidenav item belongs to, or null for items that are not sold
 * as a module. Work queues, agent workload, inbox, reports and dashboards are
 * null on purpose: the server accepts either OMNICHANNEL or CALL_CENTER for
 * queues, and the pages render their own empty state, which is the honest
 * answer anyway.
 */
export function builtinSidenavKeyToModuleCode(itemId, catalog) {
  if (!itemId || itemId.startsWith("co_")) {
    return null;
  }
  return getModuleMaps(catalog).sidenavIdToModule[itemId] || null;
}

export function filterRoutesForRouter(routesArray, accessOptions = {}) {
  const result = [];
  for (const route of routesArray) {
    if (route.collapse) {
      const filteredChildren = filterRoutesForRouter(route.collapse, accessOptions);
      const parentEntitled = isRouteKeyEntitled(route.key, accessOptions);
      if (parentEntitled || filteredChildren.length > 0) {
        result.push({ ...route, collapse: filteredChildren });
      }
      continue;
    }
    if (!route.route) {
      result.push(route);
      continue;
    }
    if (isRouteKeyEntitled(route.key, accessOptions)) {
      result.push(route);
    }
  }
  return result;
}

/**
 * Routes without the ones that belong to `hiddenModuleCodes`, recursively
 * through collapse groups; a group left empty goes too. A route key or a
 * sidenav group id identifies the module. Navigation only: the router keeps
 * every entitled route, so a link to a hidden page still opens it.
 *
 * Used for the Real Estate personas (re_hidden_modules on /module-entitlements).
 */
export function withoutHiddenModules(routesArray, hiddenModuleCodes, catalog = null) {
  const hidden = new Set([...(hiddenModuleCodes || [])].map((c) => String(c).toUpperCase()));
  if (hidden.size === 0) return routesArray;
  const moduleOf = (key) =>
    (key && (routeKeyToModuleCode(key, catalog) || builtinSidenavKeyToModuleCode(key, catalog))) || null;
  const walk = (list) =>
    (list || []).reduce((out, route) => {
      if (hidden.has(moduleOf(route.key))) return out;
      if (Array.isArray(route.collapse)) {
        const children = walk(route.collapse);
        if (route.collapse.length > 0 && children.length === 0) return out;
        out.push({ ...route, collapse: children });
        return out;
      }
      out.push(route);
      return out;
    }, []);
  return walk(routesArray);
}
